import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TerminalIcon } from "@heroicons/react/outline";
import axios from "axios";
import WebhookSubscriptionForm, {
  WebhookFormSubmit,
} from "src/containers/integrations/Webhooks/WebhookSubscriptionForm";
import HeaderNavigation from "src/components/HeaderNavigation";
import useCleanTimeout from "src/hooks/useCleanTimeout";
import { API_ROUTES, buildAPIRoute, buildRoute } from "src/router/router";
import { axiosErr } from "src/types/axios";
import * as meta from "./Meta";

export default function WebhookSubscriptionCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timeout = useCleanTimeout();

  const formSubmit: WebhookFormSubmit = async (
    formData,
    setSubmitting,
    setBackendValidationErrors,
    setBackendError
  ) => {
    setBackendValidationErrors({});
    setBackendError("");
    try {
      await axios.post(
        buildAPIRoute(API_ROUTES.WEBHOOK_SUBSCRIPTION_LIST_CREATE),
        formData
      );
      setSubmitting(false);
      setSuccess(true);
      timeout.current = setTimeout(() => {
        navigate(buildRoute("webhook-list"));
      }, 1000 * 2);
    } catch (err) {
      const errorAxios: axiosErr = err as axiosErr;
      setSuccess(false);
      if (errorAxios?.response?.status === 400) {
        console.log("error", err);
        setBackendValidationErrors(errorAxios.response.data);
      } else {
        setBackendError(
          t(
            "integrations:webhooks.error",
            "An error occurred while creating your webhook subscription. Please try again."
          )
        );
      }
      timeout.current = setTimeout(() => {
        setSubmitted(false);
      }, 1000 * 2);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: t("webhooks"),
              url: buildRoute("webhook-list"),
            },
            {
              name: t(
                "integrations:webhooks.create",
                "New Webhook Subscription"
              ),
              url: buildRoute("webhook-create"),
            },
          ]}
        />

        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              <TerminalIcon className="mr-2 h-7 w-7" aria-hidden="true" />
              {t("integrations:webhooks.create", "New Webhook Subscription")}
            </h2>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mt-6 max-w-3xl">
          <WebhookSubscriptionForm
            webhook={meta.getInitialValues()}
            formSubmit={formSubmit}
            success={success}
            submitted={submitted}
            action="create"
          />
        </div>
      </div>
    </div>
  );
}
